import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Sparkle, PaperPlaneTilt, ListChecks, CalendarPlus, ArrowClockwise, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function NextBestAction({ contactId, onDraft, onCreateTask, onScheduleMeeting }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);

  const load = useCallback(async (force = false) => {
    if (!contactId) return;
    const setter = force ? setRegen : setLoading;
    setter(true);
    try {
      const url = `/ai/contacts/${contactId}/summary${force ? "?force=true" : ""}`;
      const r = await api.post(url, {});
      setData(r.data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || e.message });
    } finally {
      setter(false);
    }
  }, [contactId]);

  useEffect(() => { load(false); }, [load]);

  if (loading) {
    return (
      <div className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="next-best-action-loading">
        <div className="overline flex items-center gap-2"><Sparkle weight="bold" style={{ color: "var(--accent)" }} /> // next best action</div>
        <div className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>Analyzing relationship…</div>
      </div>
    );
  }

  if (data?.error) {
    return (
      <div className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="next-best-action-error">
        <div className="overline flex items-center gap-2"><Warning weight="bold" style={{ color: "#FF9500" }} /> // next best action</div>
        <div className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>{data.error}</div>
      </div>
    );
  }

  const out = data?.output || {};
  const strength = out.relationship_strength || "needs_attention";
  const strengthColor = {
    strong: "#34C759",
    healthy: "var(--accent)",
    needs_attention: "#FF9500",
    dormant: "#8E8E93",
  }[strength];
  const channel = (out.suggested_channel || "email").toLowerCase();

  return (
    <div className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="next-best-action">
      <div className="flex items-center justify-between">
        <div className="overline flex items-center gap-2">
          <Sparkle weight="bold" style={{ color: "var(--accent)" }} /> // next best action
        </div>
        <div className="flex items-center gap-2">
          <span className="tag" style={{ color: strengthColor, borderColor: strengthColor }} data-testid="relationship-strength-chip">
            {strength.replace("_", " ")}
          </span>
          <button
            onClick={() => load(true)}
            className="btn-secondary text-xs px-2"
            disabled={regen}
            title="Regenerate"
            data-testid="regenerate-summary-btn"
          >
            <ArrowClockwise weight="bold" className={regen ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="mt-4 text-base leading-relaxed" data-testid="nba-recommendation">
        {out.next_best_action || "Reach out to re-establish contact."}
      </div>

      {out.summary && (
        <div className="mt-4 text-sm" style={{ color: "var(--text-2)" }} data-testid="nba-summary">
          <span className="overline" style={{ color: "var(--text-3)" }}>context · </span>
          {out.summary}
        </div>
      )}

      {out.likely_needs && (
        <div className="mt-2 text-sm" style={{ color: "var(--text-2)" }} data-testid="nba-likely-needs">
          <span className="overline" style={{ color: "var(--text-3)" }}>likely needs · </span>
          {out.likely_needs}
        </div>
      )}

      {out.last_meaningful_interaction && (
        <div className="mt-2 text-sm" style={{ color: "var(--text-2)" }} data-testid="nba-last-interaction">
          <span className="overline" style={{ color: "var(--text-3)" }}>last interaction · </span>
          {out.last_meaningful_interaction}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2" data-testid="nba-actions">
        <button
          onClick={() => onDraft?.(channel)}
          className="btn-primary flex items-center gap-2"
          data-testid="nba-draft-btn"
        >
          <PaperPlaneTilt weight="bold" /> Draft message
        </button>
        <button
          onClick={() => onCreateTask?.(out.next_best_action)}
          className="btn-secondary flex items-center gap-2"
          data-testid="nba-task-btn"
        >
          <ListChecks weight="bold" /> Create task
        </button>
        <button
          onClick={() => onScheduleMeeting?.()}
          className="btn-secondary flex items-center gap-2"
          data-testid="nba-meeting-btn"
        >
          <CalendarPlus weight="bold" /> Schedule meeting
        </button>
      </div>

      {data?.cached && (
        <div className="mt-3 text-xs" style={{ color: "var(--text-3)" }}>
          cached · click <ArrowClockwise weight="bold" className="inline" /> to regenerate
        </div>
      )}
    </div>
  );
}
